<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="600px"
    :close-on-click-modal="false"
    @close="handleClose">
    <el-form :model="form" :rules="formRules" ref="form" label-width="120px" size="small">
      <el-form-item label="选择机构" prop="organizationId">
        <el-select 
          v-model="form.organizationId" 
          placeholder="请选择机构" 
          style="width: 100%;"
          filterable
          :disabled="!!form.id">
          <el-option
            v-for="item in availableOrganizations"
            :key="item.id"
            :label="item.name"
            :value="item.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-divider content-position="left">金额限制</el-divider>
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="最低提现(元)" prop="minAmount">
            <el-input-number v-model="form.minAmount" :min="1" :max="10000" :precision="0" style="width: 100%;" @change="handleMinAmountChange"></el-input-number>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="最高提现(元)" prop="maxAmount">
            <el-input-number v-model="form.maxAmount" :min="100" :max="100000" :precision="0" style="width: 100%;"></el-input-number>
          </el-form-item>
        </el-col>
      </el-row>
      <el-divider content-position="left">费率与限制</el-divider>
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="手续费率(%)" prop="feeRate">
            <el-input-number v-model="form.feeRate" :min="0" :max="10" :precision="2" :step="0.1" style="width: 100%;"></el-input-number>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="每日次数" prop="dailyLimit">
            <el-input-number v-model="form.dailyLimit" :min="1" :max="10" style="width: 100%;"></el-input-number>
          </el-form-item>
        </el-col>
      </el-row>
      <el-divider content-position="left">到账设置</el-divider>
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="到账天数" prop="arrivalDays">
            <el-select v-model="form.arrivalDays" style="width: 100%;">
              <el-option :value="0" label="T+0 实时到账"></el-option>
              <el-option :value="1" label="T+1 次日到账"></el-option>
              <el-option :value="3" label="T+3 三日到账"></el-option>
              <el-option :value="7" label="T+7 七日到账"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="启用状态">
            <el-switch v-model="form.enabled" active-text="启用" inactive-text="禁用"></el-switch>
          </el-form-item>
        </el-col>
      </el-row>
      <!-- 规则预览 -->
      <div class="dialog-preview">
        <div class="preview-title"><i class="el-icon-view"></i> 规则预览</div>
        <div class="preview-content">
          <p>提现 <strong>¥1000</strong> 时：手续费 <strong class="text-warning">¥{{ calculateFee(1000).toFixed(2) }}</strong>，实际到账 <strong class="text-success">¥{{ calculateActualAmount(1000).toFixed(2) }}</strong></p>
        </div>
      </div>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSave" :loading="saveLoading">保存</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'OrgRuleDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    organizationList: {
      type: Array,
      default: () => []
    },
    orgRuleList: {
      type: Array,
      default: () => []
    },
    formData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      dialogVisible: false,
      form: {
        id: null,
        organizationId: '',
        minAmount: 100,
        maxAmount: 10000,
        feeRate: 0.6,
        dailyLimit: 3,
        arrivalDays: 0,
        enabled: true
      },
      formRules: {
        organizationId: [
          { required: true, message: '请选择机构', trigger: 'change' }
        ],
        minAmount: [
          { required: true, message: '请输入最低提现金额', trigger: 'blur' }
        ],
        maxAmount: [
          { required: true, message: '请输入最高提现金额', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              if (value && this.form.minAmount && value <= this.form.minAmount) {
                callback(new Error('最高提现金额必须大于最低提现金额'))
              } else {
                callback()
              }
            }, 
            trigger: 'blur' 
          }
        ],
        feeRate: [
          { required: true, message: '请输入手续费率', trigger: 'blur' }
        ],
        dailyLimit: [
          { required: true, message: '请输入每日次数', trigger: 'blur' }
        ],
        arrivalDays: [
          { required: true, message: '请选择到账天数', trigger: 'change' }
        ]
      },
      saveLoading: false
    }
  },
  computed: {
    dialogTitle() {
      return this.form.id ? '编辑机构规则' : '新增机构规则'
    },
    // 可选择的机构列表（排除已配置的）
    availableOrganizations() {
      if (this.form.id) {
        // 编辑时显示所有机构
        return this.organizationList
      }
      // 新增时排除已配置的机构
      const configuredOrgIds = this.orgRuleList.map(r => r.organizationId)
      return this.organizationList.filter(org => !configuredOrgIds.includes(org.id))
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.initForm()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    },
    formData: {
      handler(val) {
        if (val) {
          this.initForm()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    /**
     * 初始化表单数据
     * 作者：孙艺
     */
    initForm() {
      if (this.formData && this.formData.id) {
        // 编辑模式：使用传入的数据
        this.form = { ...this.formData }
      } else {
        // 新增模式：重置为默认值
        this.form = {
          id: null,
          organizationId: '',
          minAmount: 100,
          maxAmount: 10000,
          feeRate: 0.6,
          dailyLimit: 3,
          arrivalDays: 0,
          enabled: true
        }
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate()
        }
      })
    },
    /**
     * 关闭对话框
     * 作者：孙艺
     */
    handleClose() {
      this.dialogVisible = false
    },
    /**
     * 保存机构规则
     * 作者：孙艺
     */
    handleSave() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.saveLoading = true
          // 获取机构名称
          const org = this.organizationList.find(o => o.id === this.form.organizationId)
          const ruleData = {
            ...this.form,
            organizationName: org ? org.name : ''
          }
          
          this.$emit('save-success', ruleData)
          this.saveLoading = false
          this.dialogVisible = false
        }
      })
    },
    /**
     * 最低提现金额改变时，重新验证最高提现金额
     * 作者：孙艺
     */
    handleMinAmountChange() {
      if (this.$refs.form) {
        this.$refs.form.validateField('maxAmount')
      }
    },
    /**
     * 计算手续费
     * 作者：孙艺
     */
    calculateFee(amount) {
      if (!this.form.feeRate) return 0
      return amount * (parseFloat(this.form.feeRate) / 100)
    },
    /**
     * 计算实际到账金额
     * 作者：孙艺
     */
    calculateActualAmount(amount) {
      if (!this.form.feeRate) return amount
      return amount - this.calculateFee(amount)
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../channel.less';

// 对话框预览
.dialog-preview {
  margin-top: 16px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  
  .preview-title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
    
    i {
      margin-right: 6px;
      color: #409EFF;
    }
  }
  
  .preview-content {
    font-size: 13px;
    color: #606266;
    
    p {
      margin: 0;
    }
    
    .text-warning {
      color: #E6A23C;
    }
    
    .text-success {
      color: #67C23A;
    }
  }
}
</style>

